import { DateField, SelectField, TCheckboxValue, TextField, ToggleSwitch } from 'ui-toolkit';
import { useField } from 'formik';
import React, { ReactNode, useCallback } from 'react';
import { HoldbackReasonOptions } from './HoldbackReasonOptions';
import { IHoldbackPropertiesDisbursement } from 'types/order/escrow/IHoldbackDisbursement';
import { BodyTextTW } from 'tailwindComponents/bodyText/BodyTextTW';
import { DateTime } from 'luxon';
import { useGetMeQuery } from 'API/BaseApi';
import { DateFormat, getDateString } from '../../../../../../ui-toolkit/utilities/getDateString';

interface IHoldbackContainerProps {
  id: string;
  className?: string;
  holdbackWarning?: ReactNode;
  isReadOnly?: boolean;
  isDirty?: boolean;
}

const OTHER_HOLDBACK = 'Other Holdback';

export const HoldbackContainer: React.FC<IHoldbackContainerProps> = ({
  id,
  className,
  isReadOnly,
  holdbackWarning,
}) => {
  const [{ value }, , { setValue }] = useField<boolean>(`value.holdback`);
  const [{ value: holdbackProperties }, , { setValue: setHoldbackProperties }] = useField<
    IHoldbackPropertiesDisbursement | undefined
  >('value.holdbackProperties');
  const { data } = useGetMeQuery();

  const onToggle = useCallback(
    async (_: TCheckboxValue, checked: boolean) => {
      if (!checked && !!holdbackProperties?.reason && !!holdbackProperties?.dueDate) {
        await setHoldbackProperties({
          ...holdbackProperties,
          releasedDate: DateTime.now().toUTC().toString(),
          releasedBy: data?.name,
        });
      }
      await setValue(checked);
    },
    [holdbackProperties, setHoldbackProperties, setValue, data?.name]
  );

  const onChangeHoldbackReason = useCallback(
    async (newValue: string) => {
      if (newValue !== OTHER_HOLDBACK) {
        await setHoldbackProperties({ ...holdbackProperties, otherReason: undefined, reason: newValue });
      }
    },
    [holdbackProperties, setHoldbackProperties]
  );

  return (
    <div className={'my-2 flex basis-full flex-row flex-wrap gap-y-2'}>
      <ToggleSwitch
        className={className}
        onChange={onToggle}
        checked={value}
        label={'Holdback'}
        value=""
        id={`${id}.holdback`}
        disabled={isReadOnly}
      />
      <>
        <SelectField
          className={'grow px-2'}
          name={`value.holdbackProperties.reason`}
          options={HoldbackReasonOptions}
          label={'Holdback Reason'}
          isRequired
          isDisabled={!value || isReadOnly}
          onChange={onChangeHoldbackReason}
        />
        {holdbackProperties?.reason === OTHER_HOLDBACK && (
          <TextField
            className={'pr-2'}
            label={'Other Reason'}
            name={`value.holdbackProperties.otherReason`}
            isRequired
            readOnly={!value || isReadOnly}
          />
        )}
        <DateField
          name={`value.holdbackProperties.dueDate`}
          label={'Release Date'}
          isRequired
          readOnly={!value || isReadOnly}
        />
        {!value && (
          <div className={'flex basis-full flex-row justify-end gap-2'}>
            {holdbackProperties?.releasedDate && (
              <BodyTextTW>
                Released Date: {getDateString(DateFormat.shortDateTime, holdbackProperties.releasedDate, false)}
              </BodyTextTW>
            )}
            {holdbackProperties?.releasedBy && <BodyTextTW>Released By: {holdbackProperties.releasedBy}</BodyTextTW>}
          </div>
        )}
        {!!holdbackWarning && <div className={'basis-full'}>{holdbackWarning}</div>}
      </>
    </div>
  );
};
